import type { QualityMetricOrganizationConfiguration } from 'shared/api/quality-metric-rq.service.api';

// The only pull source today; each source gets its own configuration block.
export const SOURCE = 'mpog';

// Radix forbids an empty-string `SelectItem` value, so "no default parser"
// travels through the form as this sentinel and maps back to `null` on save.
export const NO_MAPPER = 'none';

export type Mapper = NonNullable<
  NonNullable<QualityMetricOrganizationConfiguration['mapper']>['default']
>;

/**
 * How a parser is shown on screen.
 *
 * @param mapper - One of `PULL_MAPPERS`.
 * @returns The name uppercased. These are system names, never translated.
 * @example
 * mapperLabel('mpog'); // 'MPOG'
 */
export const mapperLabel = (mapper: Mapper) => mapper.toUpperCase();

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const pad = (value: number | string) => String(value).padStart(2, '0');

export type SchedulePreset = 'weekly' | 'monthly' | 'custom';

export interface ScheduleDraft {
  preset: SchedulePreset;
  weekday: string;
  day: string;
  time: string;
  expression: string;
}

export const DEFAULT_SCHEDULE: ScheduleDraft = {
  preset: 'weekly',
  weekday: '4',
  day: '1',
  time: '00:00',
  expression: '0 0 * * 4',
};

export const MAX_MONTH_DAY = 28;

/**
 * Builds the five-field cron the API stores from the editor's draft.
 *
 * @param draft.preset - `weekly`, `monthly`, or `custom` to pass `expression`
 *   through untouched.
 * @param draft.weekday - `0`–`6`, Sunday first. `weekly` only.
 * @param draft.day - `1`–`MAX_MONTH_DAY`. `monthly` only; later days are not
 *   offered because they would skip short months.
 * @param draft.time - `HH:MM`, evaluated in the API's time zone, not the
 *   viewer's.
 * @param draft.expression - The raw expression. `custom` only.
 * @returns The cron expression.
 * @example
 * buildCron({ ...DEFAULT_SCHEDULE, preset: 'weekly', weekday: '4', time: '00:00' }); // '0 0 * * 4'
 */
export const buildCron = ({ preset, weekday, day, time, expression }: ScheduleDraft) => {
  if (preset === 'custom') return expression.trim();
  const [hour = '0', minute = '0'] = time.split(':');
  const minutes = Number(minute);
  const hours = Number(hour);
  if (preset === 'monthly') return `${minutes} ${hours} ${day} * *`;
  return `${minutes} ${hours} * * ${weekday}`;
};

/**
 * Best-effort inverse of {@link buildCron}, for seeding the editor from a
 * stored run.
 *
 * @param schedule - A five-field cron expression.
 * @returns The draft. Anything the presets did not produce comes back as
 *   `custom` with the expression intact, rather than being rewritten into a
 *   preset that means something else.
 * @example
 * parseCron('0 0 * * 4'); // { preset: 'weekly', weekday: '4', time: '00:00', … }
 */
export const parseCron = (schedule: string): ScheduleDraft => {
  const fallback: ScheduleDraft = { ...DEFAULT_SCHEDULE, preset: 'custom', expression: schedule };
  const parts = schedule.trim().split(/\s+/);
  if (parts.length !== 5) return fallback;

  const [minute = '', hour = '', dayOfMonth = '', month = '', weekday = ''] = parts;
  const isNumber = (value: string) => /^\d+$/.test(value);
  if (!isNumber(minute) || !isNumber(hour) || month !== '*') return fallback;

  const time = `${pad(hour)}:${pad(minute)}`;

  if (dayOfMonth === '*' && /^[0-6]$/.test(weekday)) {
    return { ...DEFAULT_SCHEDULE, preset: 'weekly', weekday, time, expression: schedule };
  }
  if (weekday === '*' && isNumber(dayOfMonth) && Number(dayOfMonth) <= MAX_MONTH_DAY) {
    return { ...DEFAULT_SCHEDULE, preset: 'monthly', day: dayOfMonth, time, expression: schedule };
  }
  return fallback;
};

/**
 * A readable form of a cron expression, for the runs table.
 *
 * @param schedule - A five-field cron expression.
 * @returns The plain-English reading, or the expression unchanged when it is
 *   not a shape the presets produce — a step, a list or a range. A wrong
 *   reading of a schedule is worse than no reading at all.
 * @example
 * describeCron('0 0 * * 4'); // 'Every Thursday at 00:00'
 * describeCron('0 0 * * 1-5'); // '0 0 * * 1-5'
 */
export const describeCron = (schedule: string) => {
  const parts = schedule.trim().split(/\s+/);
  if (parts.length !== 5) return schedule;

  const [minute = '', hour = '', dayOfMonth = '', month = '', weekday = ''] = parts;
  if (!/^\d+$/.test(minute) || !/^\d+$/.test(hour) || month !== '*') return schedule;

  const at = `${pad(hour)}:${pad(minute)}`;
  if (dayOfMonth === '*' && weekday === '*') return `Every day at ${at}`;
  if (dayOfMonth === '*' && /^[0-6]$/.test(weekday)) {
    return `Every ${WEEKDAYS[Number(weekday)]} at ${at}`;
  }
  if (weekday === '*' && /^\d+$/.test(dayOfMonth)) {
    return `Monthly on day ${dayOfMonth} at ${at}`;
  }
  return schedule;
};

/**
 * Whether a string is shaped like the cron the API accepts. A cheap form check
 * for the custom-expression field, not a parse: the API still validates it.
 *
 * @param schedule - The expression to check.
 * @returns `true` for five space-separated fields of digits, `*`, `/`, `,`
 *   and `-`.
 * @example
 * isCronLike('0 0 * * 4'); // true
 */
export const isCronLike = (schedule: string) => {
  const parts = schedule.trim().split(/\s+/);
  return parts.length === 5 && parts.every(part => /^[\d*/,-]+$/.test(part));
};

/**
 * A readable form of a run's window, which is how a run is named on screen.
 *
 * @param run.lookback - `"Nmo"`, the last N months on every tick.
 * @param run.startDate - `"MM/DD/YYYY"`, from that date until the tick.
 *   Exactly one of the two is set on a stored run.
 * @returns The description, or an em dash when neither field is set.
 * @example
 * describeWindow({ lookback: '3mo' }); // 'Last 3 months'
 */
export const describeWindow = ({
  lookback,
  startDate,
}: {
  lookback?: string;
  startDate?: string;
}) => {
  if (startDate) return `From ${startDate}`;
  if (!lookback) return '—';
  const months = Number(lookback.replace(/mo$/i, ''));
  return `Last ${months} month${months === 1 ? '' : 's'}`;
};

/**
 * Converts a date input's value to the format the runs API stores.
 *
 * @param isoDate - `"YYYY-MM-DD"`, as `<input type="date">` yields it.
 * @returns `"MM/DD/YYYY"`, or an empty string when the input is not that shape.
 * @example
 * toApiDate('2025-10-01'); // '10/01/2025'
 */
export const toApiDate = (isoDate: string) => {
  const [year, month, day] = isoDate.split('-');
  return year && month && day ? `${month}/${day}/${year}` : '';
};

/**
 * The inverse of {@link toApiDate}, for seeding a date input from a stored run.
 *
 * @param apiDate - `"MM/DD/YYYY"`, as the API stores it.
 * @returns `"YYYY-MM-DD"`, or an empty string when the input is not that shape.
 * @example
 * toInputDate('10/01/2025'); // '2025-10-01'
 */
export const toInputDate = (apiDate: string) => {
  const [month, day, year] = apiDate.split('/');
  return year && month && day ? `${year}-${pad(month)}-${pad(day)}` : '';
};

/**
 * The Cloud Scheduler console page for a job.
 *
 * @param name - The resource name the API returns,
 *   `projects/<project>/locations/<region>/jobs/<job>`.
 * @returns The console URL, or `null` when the name is not that shape — a
 *   format change then drops the shortcut rather than sending an operator to a
 *   dead console page.
 * @example
 * const url = schedulerJobUrl(run.scheduler?.name);
 */
export const schedulerJobUrl = (name: string | null | undefined) => {
  const match = /^projects\/([^/]+)\/locations\/([^/]+)\/jobs\/([^/]+)$/.exec(name ?? '');
  if (!match) return null;
  const [, project, location, job] = match;
  return `https://console.cloud.google.com/cloudscheduler/jobs/edit/${location}/${job}?project=${project}`;
};
